/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantContainer = this.querySelector('.sticky-atc__details__variant');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        // Hide the sticky bar once the footer scrolls into view.
        const footer = document.querySelector('.section-footer');
        if (footer && 'IntersectionObserver' in window) {
          this.footerObserver = new IntersectionObserver((entries) => {
            document.body.classList.toggle('footer-in-view', entries[0].isIntersecting);
          });
          this.footerObserver.observe(footer);
        }

        // The reward bar is pinned on mobile, and its height moves around
        // (tier message wraps, savings badge appears, the status block is
        // re-rendered on every cart change). Publish it so the stylesheet can
        // pad the body and keep the footer reachable.
        if (this.classList.contains('sticky-reward-atc') && 'ResizeObserver' in window) {
          this.heightObserver = new ResizeObserver(([entry]) => {
            document.documentElement.style.setProperty(
              '--sticky-reward-atc-h',
              `${entry.target.offsetHeight}px`
            );
          });
          this.heightObserver.observe(this);
        }

        // The bar renders its own button from current_variant, but the main
        // form's Add to Cart is the source of truth (variant-picker keeps it
        // disabled for a sold-out selection, and it is re-rendered by section
        // updates). Mirror it on mount and on every change, so the bar can
        // never offer a sold-out variant even if its own markup says otherwise.
        this.mainSubmitBtn = this.buyButtons?.querySelector('[name="add"]');
        if (this.mainSubmitBtn) {
          this.syncSubmitDisabled();
          this.disabledObserver = new MutationObserver(this.syncSubmitDisabled.bind(this));
          this.disabledObserver.observe(this.mainSubmitBtn, {
            attributes: true,
            attributeFilter: ['disabled']
          });
        }

        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        this.updateImage();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
        this.footerObserver?.disconnect();
        this.heightObserver?.disconnect();
        this.disabledObserver?.disconnect();
        document.documentElement.style.removeProperty('--sticky-reward-atc-h');
        document.body.classList.remove('footer-in-view');
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        // Sold out: nothing to add. A disabled button already swallows its own
        // clicks, but the form can still be submitted by Enter in a field or by
        // a third-party script, so the guard lives here as well.
        if (this.submitBtn?.disabled || this.mainSubmitBtn?.disabled) return;

        const variantId = this.section.querySelector('product-form buy-buttons input[name="id"]').value;
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Copy data
        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      static handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
        this.classList.toggle(
          'sticky-atc--out',
          this.buyButtons.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');

        // The variant nodes are optional: the reward edition of the bar
        // (snippets/sticky-reward-atc.liquid) renders no product/variant block.
        if (evt.detail.variant) {
          if (this.variantTitle) this.variantTitle.textContent = evt.detail.variant.title;
          if (this.variantContainer) this.variantContainer.hidden = false;
          if (idInput) idInput.value = evt.detail.variant.id;
        } else {
          if (this.variantContainer) this.variantContainer.hidden = true;
          if (this.variantTitle) this.variantTitle.textContent = '';
          if (idInput) idInput.value = '';
        }

        this.updateImage();
        this.updateAddToCartButton(evt.detail.variant);
        this.setErrorMsgState();
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Mirrors the main form's Add to Cart disabled state onto the bar's button.
       */
      syncSubmitDisabled() {
        if (!this.submitBtn || !this.mainSubmitBtn) return;
        this.submitBtn.disabled = this.mainSubmitBtn.disabled;
        if (this.submitBtn.disabled) {
          this.submitBtn.textContent = theme.strings.noStock;
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        const variantAvailable = variant && variant.available;
        const unavailableStr = variant
          ? theme.strings.noStock : this.submitBtn.dataset.unavailableText;

        // Only a selected-but-sold-out variant disables the button; with no
        // variant selected it stays clickable so handleSubmit can scroll to
        // the picker ("view details" behavior).
        this.submitBtn.disabled = !!variant && !variant.available;
        this.submitBtn.textContent = variantAvailable
          ? this.submitBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
